import { Box, Grid, IconButton, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useRef } from "react";
import {
    NumberInput,
    ReferenceInput,
    required,
    SimpleForm,
    useGetList,
    useLocaleState,
    useNotify,
    useTranslate,
} from "react-admin";
import CloseIcon from "@mui/icons-material/Close";
import { RaAutocompleteInput } from "../../components/inputs/Ra/RaAutocompleteInput";
import { RaSelectInput } from "shared/src/components/inputs/Ra/RaSelectInput";
import { FileManagerContext } from "./FileManagerContext";
import { SerialNumberField } from "../components/SerialNumberField";
import { handleHttpError } from "shared/src/utils/errorHandler";

export const ProductForm = ({ record, index, removeProduct }) => {
    const { scheduleUpdate, data, isCheckingProductForms, setIsCheckingProductForms } = useContext(FileManagerContext);
    const productFormRef = useRef();
    const translate = useTranslate();
    const notify = useNotify();
    const [locale] = useLocaleState();

    const currentRecord = data?.productList?.[index] ?? record;
    
    const isSerialNumberRequired = !+currentRecord?.isMassProduct; // Individual products need serial numbers
    const hasSerialNumberError = isSerialNumberRequired && (!currentRecord?.serialNumbers || currentRecord?.serialNumbers.length === 0);

    useGetList(
        "products",
        {
            filter: { name: currentRecord?.product },
            pagination: {
                page: 1,
                perPage: 1,
            },
            sort: { field: "id", order: "DESC" },
        },
        {
            enabled: !!currentRecord?.product && !currentRecord?.product?.id, // Only fetch if product is defined,
            onSuccess: ({ data }) => {
                setFetchedData("product", data[0]);
            },
            onError: (e) => {
                handleHttpError(e, notify);
            },
        }
    );
    useGetList(
        "manufacturers",
        {
            filter: { name: currentRecord?.manufacturer },
            pagination: {
                page: 1,
                perPage: 1,
            },
            sort: { field: "id", order: "DESC" },
        },
        {
            enabled: !!currentRecord?.manufacturer && !currentRecord?.manufacturer?.id,
            onSuccess: ({ data }) => {
                setFetchedData("manufacturer", data[0]);
            },
            onError: (e) => {
                handleHttpError(e, notify);
            },
        }
    );

    const setFetchedData = useCallback(
        (field, fetchedValue) => {
            const normalizedValue = fetchedValue ?? null;

            scheduleUpdate((prev) => {
                const nextProductList = [...(prev.productList || [])];
                const currentItem = nextProductList[index] || {};

                if (currentItem[field] === normalizedValue) {
                    return prev;
                }

                nextProductList[index] = {
                    ...currentItem,
                    [field]: normalizedValue,
                };

                return {
                    ...prev,
                    productList: nextProductList,
                };
            });
        },
        [index, scheduleUpdate]
    );

    const handleChange = useCallback(
        ({ name, value }) => {
            if (!name) return;
            if (["product.id", "storageLocation.id", "manufacturer.id"].includes(name)) return;

            scheduleUpdate((prev) => {
                const nextProductList = [...(prev.productList || [])];
                const currentItem = nextProductList[index] || {};

                if (currentItem[name] === value) {
                    return prev;
                }

                nextProductList[index] = {
                    ...currentItem,
                    [name]: value,
                };

                return {
                    ...prev,
                    productList: nextProductList,
                };
            });
        },
        [index, scheduleUpdate]
    );

    useEffect(() => {
        if (isCheckingProductForms && productFormRef.current) {
            const form = productFormRef.current.querySelector('form');
            if (form) {
                form.requestSubmit();
            }
            if (index === (data?.productList?.length || 0) - 1) {
                setIsCheckingProductForms(false);
            }
        }
    }, [isCheckingProductForms, index, data?.productList?.length, setIsCheckingProductForms]);

    useEffect(() => {
        const hasSerialNumbers =
            Array.isArray(currentRecord?.serialNumbers) && currentRecord.serialNumbers.length > 0;

        if (!hasSerialNumbers || !+currentRecord?.isMassProduct) {
            return;
        }

        handleChange({ name: "isMassProduct", value: "0" });
    }, [currentRecord?.isMassProduct, currentRecord?.serialNumbers, handleChange]);

    const handleSubmit = () => {
        return Promise.resolve();
    };

    return (
        <Box ref={productFormRef}>
            <SimpleForm
                onSubmit={handleSubmit}
            onChange={(e, b, c) => {
                const { name, value } = e.target;
                handleChange({ name, value });
            }}
            toolbar={<></>}
            record={{
                ...currentRecord,
            }}
            sx={{ mb: 2, py: "0!important" }}
        >
            <Box sx={{ position: "relative", width: "100%" }}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        position: "absolute",
                        top: -15,
                        left: 8,
                        px: 1,
                        width: "auto",
                        display: "flex",
                        backgroundColor: "#1e1e1e",
                    }}
                >
                    {`${translate("equip.product")} ${index + 1}`}
                </Typography>
                <IconButton
                    sx={{
                        position: "absolute",
                        top: -15,
                        right: 0,
                        px: 1,
                        width: "auto",
                        display: "flex",
                        backgroundColor: "#1e1e1e",
                        borderRadius: "50%",
                        border: "1px solid #636363",
                        "&:hover": {
                            backgroundColor: "#2e2e2e",
                        },
                    }}
                    onClick={() => removeProduct(index)}
                >
                    <CloseIcon
                        sx={{
                            height: "10px",
                            width: "10px",
                        }}
                    />
                </IconButton>
            </Box>
            <Grid
                container
                spacing={1}
                sx={{
                    border: "1px solid #636363",
                    p: 1,
                    mb: 2,
                    mt: 0,
                    borderRadius: 1,
                    overflow: "hidden",
                }}
            >
                <Grid
                    item
                    size={{ sm: 6, lg: 4 }}
                    sx={{ mb: "0!important", maxHeight: "60px!important" }}
                >
                    <RaSelectInput
                        source="isMassProduct"
                        label="equip.type"
                        choices={[
                            {
                                id: "0",
                                name: "equip.individualSkus",
                            },
                            { id: "1", name: "equip.massProduct" },
                        ]}
                        onChange={({ target }) => {
                            handleChange({ value: target.value, name: "isMassProduct" });
                        }}
                        alwaysOn
                    />
                </Grid>
                <Grid
                    item
                    size={{ sm: 6, lg: 4 }}
                    sx={{ mb: "0!important", maxHeight: "60px!important" }}
                >
                    <ReferenceInput
                        source="product.id"
                        reference="products"
                        sort={{ field: "name", order: "ASC" }}
                    >
                        <RaAutocompleteInput
                            label="equip.product"
                            validate={[required()]}
                            fullWidth
                            field="name"
                            optionText={({ name }) => name?.[locale] || ""}
                            inputText={(choice) => choice?.name?.[locale] || ""}
                            onChange={(event, value) => {
                                handleChange({ name: "product", value });
                            }}
                        />
                    </ReferenceInput>
                </Grid>
                <Grid size={{ sm: 6, lg: 4 }} sx={{ maxHeight: "60px!important" }}>
                    <ReferenceInput
                        source="storageLocation.id"
                        reference="storageLocations"
                        sort={{ field: "name", order: "ASC" }}
                    >
                        <RaAutocompleteInput
                            field="name"
                            label="equip.storageLocation"
                            onChange={(event, value) => {
                                handleChange({ name: "storageLocation", value });
                            }}
                        />
                    </ReferenceInput>
                </Grid>
                <Grid
                    item
                    size={{ sm: 6, lg: 4 }}
                    sx={{ mb: "0!important", maxHeight: "60px!important" }}
                >
                    <ReferenceInput
                        source="manufacturer.id"
                        reference="manufacturers"
                        sort={{ field: "name", order: "ASC" }}
                    >
                        <RaAutocompleteInput
                            field="name"
                            label="equip.manufacturer"
                            validate={[required()]}
                            onChange={(event, value) => {
                                handleChange({ name: "manufacturer", value });
                            }}
                        />
                    </ReferenceInput>
                </Grid>
                <Grid
                    item
                    size={{ sm: 6, lg: 4 }}
                    sx={{ mt: "0!important", maxHeight: "60px!important" }}
                >
                    <NumberInput
                        title={translate("equip.purchasePrice")}
                        label="equip.purchasePrice"
                        source="purchasePrice"
                        fullWidth
                        required
                    />
                </Grid>
                <Grid
                    item
                    size={{ sm: +currentRecord?.isMassProduct ? 6 : 6, lg: 4 }}
                    sx={{ maxHeight: "60px!important" }}
                >
                    <NumberInput
                        title={translate("equip.weight")}
                        label="equip.weight"
                        source="weight"
                        fullWidth
                    />
                </Grid>
                {+currentRecord?.isMassProduct ? (
                    <Grid
                        item
                        size={{ sm: +currentRecord?.isMassProduct ? 12 : 6 }}
                        sx={{ maxHeight: "60px!important" }}
                    >
                        <NumberInput
                            title={translate("equip.quantity")}
                            label="equip.quantity"
                            source="quantity"
                            fullWidth
                        />
                    </Grid>
                ) : (
                    <></>
                )}
                {!+currentRecord?.isMassProduct && (
                    <Grid size={{ sm: 12 }}>
                        <SerialNumberField
                            serialNumbers={currentRecord?.serialNumbers || []}
                            setSerialNumbers={(serialNumbers) => {
                                const newValue =
                                    typeof serialNumbers === "function"
                                        ? serialNumbers(currentRecord?.serialNumbers || [])
                                        : serialNumbers;
                                handleChange({ name: "serialNumbers", value: newValue });
                            }}
                            required={isSerialNumberRequired}
                            error={hasSerialNumberError && isCheckingProductForms}
                        />
                    </Grid>
                )}
            </Grid>
        </SimpleForm>
        </Box>
    );
};
